import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Form from "@/components/ui/Form";
import ViewWrapper from "@/components/ui/ViewWrapper";
import { SupabaseInstance } from "@/integrations/supabase";
import { useToast } from "@/hooks/useToast";
import { ForgotPasswordSchema, type ForgotPasswordType } from "./form";
import { TOAST_MESSAGES } from "./constants";
import { Logger } from "@/utils";
import { useResendCooldown } from "@/hooks/useResendCooldown";
import EmailAffordanceCard from "@/components/EmailAffordanceCard";

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const { secondsLeft, isCoolingDown, start: startCooldown } = useResendCooldown();
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordType>({
    resolver: zodResolver(ForgotPasswordSchema),
  });
  const onSubmit = handleSubmit((data) => {
    setIsLoading(true);
    SupabaseInstance.resetPasswordForEmail(data.email)
      .then(({ error }) => {
        if (error) {
          if (error.code === "over_email_send_rate_limit") {
            handleShowToast("OVER_EMAIL_SEND_RATE_LIMIT");
            setSubmittedEmail(data.email);
            startCooldown();
            return;
          }
          handleShowToast("GENERIC_ERROR");
          return;
        }
        handleShowToast("SUCCESS");
        setSubmittedEmail(data.email);
        startCooldown();
      })
      .catch((_) => handleShowToast("SERVER_ERROR"))
      .finally(() => setIsLoading(false));
  });

  const handleShowToast = useCallback(
    (type: keyof typeof TOAST_MESSAGES) => {
      toast(TOAST_MESSAGES[type]);
    },
    [toast]
  );

  const handleResendRecovery = useCallback(() => {
    if (!submittedEmail || isCoolingDown || isResending) return;
    setIsResending(true);
    SupabaseInstance.resetPasswordForEmail(submittedEmail)
      .then(({ error }) => {
        if (error) {
          Logger.loggerError({
            section: "resend_recovery_error",
            message: error,
          });
          if (error.code === "over_email_send_rate_limit") {
            handleShowToast("OVER_EMAIL_SEND_RATE_LIMIT");
            startCooldown();
            return;
          }
          handleShowToast("GENERIC_ERROR");
          return;
        }
        handleShowToast("RESEND_SUCCESS");
        startCooldown();
      })
      .catch((_) => handleShowToast("SERVER_ERROR"))
      .finally(() => setIsResending(false));
  }, [submittedEmail, isCoolingDown, isResending, startCooldown, handleShowToast]);

  return (
    <ViewWrapper>
      <div className="mx-auto px-6 py-8 bg-white rounded-lg shadow-md border border-gray-200">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-startfly-black mb-6">
            Recuperar contraseña
          </h1>
        </div>
        <Form.ErrorView
          isVisible={!!errors.email}
          title={errors.email?.message}
        />
        <Form.Container onSubmit={onSubmit}>
          <Form.Field
            {...register("email")}
            label="Correo Electrónico"
            labelHtmlFor="email"
            id="email"
            type="email"
            placeholder="tu@email.com"
            error={errors.hasOwnProperty("email")}
          />
          <Form.SubmitButton
            isLoading={isLoading}
            title="Continuar"
            loadingText="Verificando..."
          />
        </Form.Container>
        {submittedEmail && (
          <EmailAffordanceCard
            email={submittedEmail}
            title="Revisa tu correo para continuar"
            helperText="¿No te llegó? Revisa spam o promociones."
            buttonIdleText="Reenviar correo de recuperación"
            isCoolingDown={isCoolingDown}
            isResending={isResending}
            secondsLeft={secondsLeft}
            onResend={handleResendRecovery}
          />
        )}
      </div>
    </ViewWrapper>
  );
}
